import {
  Box, Button, HStack, Icon, Menu, MenuButton, MenuDivider, MenuItem, MenuList,
  Portal, Spacer, VStack,
} from "@chakra-ui/react";
import { FiArrowDown, FiArrowUp, FiCheck, FiChevronDown } from "react-icons/fi";
import { TbArrowsSort, TbCalendarEvent, TbCalendarTime, TbGripVertical, TbUserCheck } from "react-icons/tb";
import CDS from "design-system";
import type { TemplateSortMode } from "../types";

interface Props {
  sort: TemplateSortMode;
  onChange: (next: TemplateSortMode) => void;
}

const TRIGGER_LABEL: Record<TemplateSortMode, string> = {
  starts_asc: "Starts on",
  starts_desc: "Starts on",
  due_asc: "Due on",
  due_desc: "Due on",
  assignee: "Assignee",
  custom: "Custom",
};

const isStarts = (s: TemplateSortMode) => s === "starts_asc" || s === "starts_desc";
const isDue = (s: TemplateSortMode) => s === "due_asc" || s === "due_desc";

function SortMenuItem({
  icon, title, subtitle, active, indicator, onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  active: boolean;
  indicator?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <MenuItem onClick={onClick} py={2.5}>
      <HStack spacing={3} align="center" w="100%">
        <Box color="gray.700" display="flex" alignItems="center" justifyContent="center" w="22px" h="22px" flexShrink={0}>
          {icon}
        </Box>
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <CDS.Text12 fontWeight="semibold" color="gray.900">{title}</CDS.Text12>
          <CDS.Text10 color="gray.500">{subtitle}</CDS.Text10>
        </VStack>
        <Spacer />
        {indicator}
        {active && !indicator && <Icon as={FiCheck} color="blue.500" boxSize={4} />}
      </HStack>
    </MenuItem>
  );
}

/**
 * Sort dropdown for the task (row) axis. Lives next to the people Sort menu.
 *
 * Starts sorts by start date (earliest task per template) and breaks ties by
 * due date; Due is the mirror — primary due, tiebreak start. Both toggle
 * asc/desc on click. Assignee groups templates by their owner role; Custom
 * uses the manual template_order (set by row drag).
 */
export default function TemplateSortMenu({ sort, onChange }: Props) {
  const toggleStarts = () => onChange(sort === "starts_asc" ? "starts_desc" : "starts_asc");
  const toggleDue = () => onChange(sort === "due_asc" ? "due_desc" : "due_asc");
  const startsDir = sort === "starts_desc" ? "desc" : "asc";
  const dueDir = sort === "due_desc" ? "desc" : "asc";

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        size="sm"
        variant="outline"
        colorScheme="gray"
        leftIcon={<Icon as={TbArrowsSort} boxSize={4} />}
        rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
        fontWeight="normal"
        h="28px"
        minH="28px"
      >
        Tasks: {TRIGGER_LABEL[sort]}
      </MenuButton>
      <Portal>
        <MenuList minW="320px" py={2}>
          <CDS.Text10
            px={4}
            py={2}
            color="gray.500"
            letterSpacing="wide"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Sort tasks by
          </CDS.Text10>

          <SortMenuItem
            icon={<Icon as={TbCalendarEvent} color="gray.700" boxSize={5} />}
            title="Task starts on"
            subtitle={startsDir === "asc" ? "beginning → end" : "end → beginning"}
            active={isStarts(sort)}
            indicator={isStarts(sort) ? (
              <HStack spacing={1} color="gray.600">
                <Icon as={startsDir === "asc" ? FiArrowUp : FiArrowDown} boxSize={3.5} />
                <CDS.Text10>{startsDir}</CDS.Text10>
              </HStack>
            ) : undefined}
            onClick={toggleStarts}
          />

          <SortMenuItem
            icon={<Icon as={TbCalendarTime} color="gray.700" boxSize={5} />}
            title="Task due on"
            subtitle={dueDir === "asc" ? "beginning → end" : "end → beginning"}
            active={isDue(sort)}
            indicator={isDue(sort) ? (
              <HStack spacing={1} color="gray.600">
                <Icon as={dueDir === "asc" ? FiArrowUp : FiArrowDown} boxSize={3.5} />
                <CDS.Text10>{dueDir}</CDS.Text10>
              </HStack>
            ) : undefined}
            onClick={toggleDue}
          />

          <SortMenuItem
            icon={<Icon as={TbUserCheck} color="gray.700" boxSize={5} />}
            title="Assignee"
            subtitle="group by role"
            active={sort === "assignee"}
            onClick={() => onChange("assignee")}
          />

          <MenuDivider />

          <SortMenuItem
            icon={<Icon as={TbGripVertical} color="gray.500" boxSize={5} />}
            title="Custom"
            subtitle="your manual order"
            active={sort === "custom"}
            onClick={() => onChange("custom")}
          />
        </MenuList>
      </Portal>
    </Menu>
  );
}
